// filepath: apps/web/src/admin/people/detail.tsx
//
// ISS-518 — PersonDetailScreen.
//
// Read-only detail view. Renders the server-stripped typed
// columns (`display_name`, `email`, `phone`, `status`,
// `keycloak_subject`, `created_at`, `created_by_subject` --
// absent fields per FR-AUTH-7 render as "—"). Includes the
// Archive button that calls DELETE /api/people/{id} (Go
// handler at handlers.go:417 -- soft-deletes the row by
// setting status='inactive').
//
// INV-7: the Archive button carries row_version; the server
// returns 409 + code='row_version_mismatch' on stale submission
// and the SPA surfaces a StaleVersionError (reload to fetch
// fresh row_version).
//
// INV-9: no dangerouslySetInnerHTML; every value flows through
// JSX text / design-system primitives.

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { formatDatePattern } from "@/i18n/format";
import { Card } from "@/design-system/Card";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Banner } from "@/design-system/Banner";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { ApiError } from "@/definitions/types";
import { navigate } from "@/features/cabinet/usePathname";
import { archivePerson, getPerson, newIdempotencyKey } from "./api";
import type { Person } from "@/features/directory/types";

function handleSpaNavigate(
  e: React.MouseEvent<HTMLAnchorElement>,
  href: string,
): void {
  if (e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) {
    return;
  }
  e.preventDefault();
  navigate(href);
}

function isPermissionDenied(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 403 || e.status === 404;
  return false;
}

function isRowVersionConflict(e: unknown): boolean {
  if (e instanceof ApiError) {
    return e.status === 409 && e.code === "row_version_mismatch";
  }
  return false;
}

export interface PersonDetailScreenProps {
  id?: string;
  loader?: (id: string) => Promise<Person>;
  archiver?: (
    id: string,
    expectedRowVersion: number,
    key: string,
  ) => Promise<void>;
}

export function PersonDetailScreen({
  id = "",
  loader,
  archiver,
}: PersonDetailScreenProps = {}): React.ReactElement {
  const { t, locale } = useTranslation();
  const [row, setRow] = useState<Person | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [archiving, setArchiving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);
  const [stale, setStale] = useState<boolean>(false);
  const [archived, setArchived] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setPermissionDenied(false);
    setStale(false);
    setArchived(false);
    try {
      const f = loader ?? ((pid: string) => getPerson(pid));
      const r = await f(id);
      setRow(r);
    } catch (e) {
      if (isPermissionDenied(e)) {
        setPermissionDenied(true);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  }, [id, loader]);

  useEffect(() => {
    void load();
  }, [load]);

  const onArchive = useCallback(async () => {
    if (!row) return;
    setArchiving(true);
    setError(null);
    setStale(false);
    try {
      const key = newIdempotencyKey();
      const a = archiver ?? archivePerson;
      await a(row.id, row.row_version, key);
      setArchived(true);
      await load();
    } catch (e) {
      if (isRowVersionConflict(e)) {
        setStale(true);
      } else if (isPermissionDenied(e)) {
        setPermissionDenied(true);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setArchiving(false);
    }
  }, [row, archiver, load]);

  if (loading) {
    return (
      <p data-testid="person-detail-loading">
        {t("directory.people.detail.loading", "Loading person…")}
      </p>
    );
  }
  if (permissionDenied) {
    return (
      <div data-testid="person-detail-permission-denied">
        <PermissionDenied />
      </div>
    );
  }
  if (error && !row) {
    return (
      <p role="alert" data-testid="person-detail-error">
        {t("directory.people.detail.error", "Error: {{message}}", {
          message: error,
        })}
      </p>
    );
  }
  if (!row) {
    return (
      <p data-testid="person-detail-not-found">
        {t("directory.people.detail.not_found", "Person not found")}
      </p>
    );
  }

  return (
    <section data-testid="person-detail-screen">
      <header>
        <h1 id="person-detail-title">
          {row.display_name ?? t("directory.people.unnamed", "Unnamed person")}
        </h1>
        <div className="person-detail-actions">
          <a
            href={`/admin/people/${encodeURIComponent(row.id)}/edit`}
            data-testid="person-detail-edit-link"
            onClick={(e) =>
              handleSpaNavigate(
                e,
                `/admin/people/${encodeURIComponent(row.id)}/edit`,
              )
            }
            style={{ textDecoration: "none" }} // design-adherence-ignore: link reset (button-styled anchor; no underline)
          >
            <Button variant="primary">
              {t("directory.people.detail.edit", "Edit")}
            </Button>
          </a>
          <a
            href="/admin/people"
            data-testid="person-detail-back-link"
            onClick={(e) => handleSpaNavigate(e, "/admin/people")}
            style={{ textDecoration: "none" }} // design-adherence-ignore: link reset
          >
            <Button variant="secondary">
              {t("directory.people.detail.back", "Back")}
            </Button>
          </a>
        </div>
      </header>

      {archived ? (
        // design-adherence-ignore: <Banner> doesn't accept data-testid;
        // hook on a wrapping span instead.
        <span data-testid="person-detail-archived">
          <Banner tone="info">
            {t("directory.people.detail.archived", "Person archived.")}
          </Banner>
        </span>
      ) : null}
      {stale ? (
        <StaleVersionError
          entityName={row.display_name ?? row.id}
          onReload={() => void load()}
          data-testid="person-detail-stale"
        />
      ) : null}

      <Card>
        <dl>
          <dt>{t("directory.people.detail.display_name", "Display name")}</dt>
          <dd>{row.display_name ?? "—"}</dd>
          <dt>{t("directory.people.detail.email", "Email")}</dt>
          <dd>{row.email ?? "—"}</dd>
          <dt>{t("directory.people.detail.phone", "Phone")}</dt>
          <dd>{row.phone ?? "—"}</dd>
          <dt>{t("directory.people.detail.status", "Status")}</dt>
          <dd>
            {row.status ? (
              <Badge tone={row.status === "active" ? "success" : "neutral"}>
                {t(`directory.people.status.${row.status}`, row.status)}
              </Badge>
            ) : (
              "—"
            )}
          </dd>
          <dt>
            {t("directory.people.detail.keycloak_subject", "Keycloak subject")}
          </dt>
          <dd>{row.keycloak_subject ?? "—"}</dd>
          <dt>{t("directory.people.detail.row_version", "Row version")}</dt>
          <dd>{row.row_version}</dd>
          <dt>{t("directory.people.detail.created_at", "Created at")}</dt>
          <dd>{formatDatePattern(new Date(row.created_at), locale)}</dd>
          <dt>
            {t(
              "directory.people.detail.definition_version",
              "Definition version",
            )}
          </dt>
          <dd>{row.definition_version}</dd>
        </dl>
        {row.status !== "archived" ? (
          <div className="person-detail-archive-section">
            {/* design-adherence-ignore: <Button> doesn't accept data-testid;
                wrap in a span to keep the test hook. */}
            <span data-testid="person-detail-archive-button">
              <Button
                type="button"
                variant="danger"
                onClick={() => void onArchive()}
                disabled={archiving}
              >
                {archiving
                  ? t("directory.people.detail.archiving", "Archiving…")
                  : t("directory.people.detail.archive", "Archive")}
              </Button>
            </span>
          </div>
        ) : null}
      </Card>
    </section>
  );
}

export default PersonDetailScreen;
